import { graphql } from 'react-relay';
import { useCallback } from 'react';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';
import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { readQueryInterop } from '@confluence/apollo-relay-interop/entry-points/readQuery.ts';
import { getLogger } from '@confluence/logger';
import { fg } from '@confluence/feature-gating';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { confluenceSessionStorageInstance as sessionStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import type {
	useFeatureDiscoveryMarkFeatureDiscoveredMutation$data,
	useFeatureDiscoveryMarkFeatureDiscoveredMutation$variables,
} from './__generated__/useFeatureDiscoveryMarkFeatureDiscoveredMutation.graphql.ts';
import type {
	useFeatureDiscoveryFeatureDiscoveryQuery$data,
	useFeatureDiscoveryFeatureDiscoveryQuery$variables,
} from './__generated__/useFeatureDiscoveryFeatureDiscoveryQuery.graphql.ts';
import type { MarkFeatureDiscoveredMutation as MarkFeatureDiscoveredMutationType } from './__types__/MarkFeatureDiscoveredMutation.ts';

const useFeatureDiscoveryFeatureDiscoveryQuery = graphql`
	query useFeatureDiscoveryFeatureDiscoveryQuery {
		featureDiscovery {
			pluginKey
			featureKey
		}
	}
`;

const useFeatureDiscoveryMarkFeatureDiscoveredMutation = graphql`
	mutation useFeatureDiscoveryMarkFeatureDiscoveredMutation(
		$featureKey: String!
		$pluginKey: String!
	) {
		markFeatureDiscovered(featureKey: $featureKey, pluginKey: $pluginKey) {
			pluginKey
			featureKey
		}
	}
`;

const logger = getLogger('useFeatureDiscovery');
export const PLUGIN_KEY = 'com.atlassian.confluence.plugins.confluence-frontend-support';

const updateCache = (_: never, { data }: { data?: MarkFeatureDiscoveredMutationType }) => {
	const newMarkedFeature = data?.markFeatureDiscovered;
	if (!newMarkedFeature) return;

	const cachedFeatureData = readQueryInterop<useFeatureDiscoveryFeatureDiscoveryQuery$data>({
		query: useFeatureDiscoveryFeatureDiscoveryQuery,
	});

	if (!cachedFeatureData?.featureDiscovery) return;

	writeQueryInterop<useFeatureDiscoveryFeatureDiscoveryQuery$data>({
		query: useFeatureDiscoveryFeatureDiscoveryQuery,
		data: {
			featureDiscovery: cachedFeatureData.featureDiscovery.concat(newMarkedFeature),
		},
	});
};

const getLocalOverrideValue = (featureKey: string): boolean => {
	try {
		if (
			sessionStorage.getItem(
				fg('confluence_browser_storage_controls')
					? `${featureKey}-featureDiscoveryOverride`
					: `${keys.FEATURE_DISCOVERY_OVERRIDE_PREFIX}-${featureKey}`,
			)
		) {
			return true;
		}
	} catch (e) {
		logger.error(e);
	}
	return false;
};

const noop = () => {};

/**
 * @deprecated Please use `useOnboardingState` instead.
 */
export type UseFeatureDiscoveryResponse = [boolean, () => void, Error | undefined];

/**
 * @deprecated Please use `useOnboardingState` instead.
 */
export const useFeatureDiscovery = (
	featureKey: string,
	skip?: boolean,
): UseFeatureDiscoveryResponse => {
	const { isLoggedIn } = useSessionData();
	const skipFeatureDiscoveryQuery = !isLoggedIn || skip;

	const {
		data,
		loading: queryLoading,
		error: queryError,
	} = useQueryInterop<
		useFeatureDiscoveryFeatureDiscoveryQuery$data,
		useFeatureDiscoveryFeatureDiscoveryQuery$variables
	>(useFeatureDiscoveryFeatureDiscoveryQuery, {
		skip: skipFeatureDiscoveryQuery,
	});
	const [markFeatureDiscovered, { loading: mutationLoading, error: mutationError }] =
		useMutationInterop<
			useFeatureDiscoveryMarkFeatureDiscoveredMutation$data,
			useFeatureDiscoveryMarkFeatureDiscoveredMutation$variables
		>(useFeatureDiscoveryMarkFeatureDiscoveredMutation, {
			variables: {
				featureKey,
				pluginKey: PLUGIN_KEY,
			},
			update: updateCache,
		});
	const loading = queryLoading || mutationLoading;
	const error = queryError || mutationError;

	const showFeature =
		!loading &&
		!error &&
		!data?.featureDiscovery?.some(
			(feature) => feature?.pluginKey === PLUGIN_KEY && feature?.featureKey === featureKey,
		);

	const handleStop = useCallback(() => {
		if (showFeature) {
			// error is handled in the useMutation result
			markFeatureDiscovered().catch(() => {});
		}
	}, [showFeature, markFeatureDiscovered]);

	if (skipFeatureDiscoveryQuery) {
		return [false, noop, undefined];
	}

	// this is for dev/testing, since we cant easily remove data from the featureDiscovery array
	const forceShowFeature = showFeature || getLocalOverrideValue(featureKey);

	return [forceShowFeature, handleStop, error];
};
